import type { z, ZodType } from "zod";

import type { ActionEntry } from "../action";
import type { BranchID, ViewID } from "../types/ids";

// ============================================================================
// Interaction Types
// ============================================================================

/**
 * Navigate to an internal view by its view ID.
 *
 * The view ID is resolved to a route template by the server at loader
 * time, but the URL itself is compiled on the client at click time. Any
 * `:name` tokens in the route — and in `query` values — are filled from
 * the merge of the current request's path params and any params the
 * caller hands the navigation handler at the click site:
 *
 * ```ts
 * // Module receives `props.toUser: (params?: Record<string, string>) => void`
 * props.toUser({ userId: row.id });
 * ```
 *
 * Authoring stays declarative: the interaction names a target by `viewId`
 * (and optional `branchId`) and lists any static query keys. The
 * route-template is treated as the single source of truth for which
 * params it needs; nothing about it has to be re-declared on the
 * interaction.
 *
 * Cross-branch navigation: provide `branchId` to target a view registered
 * on a different branch within the same flow. Omit `branchId` to default to
 * the matched view's own branch (the common case). Cross-flow navigation is
 * out of scope — use `externalUrl` for that.
 */
export type ViewNavigation = {
  /** View ID (resolved to a route template at loader time) */
  viewId: ViewID;
  /**
   * Optional: target branch within the same flow. Omit to navigate within
   * the matched view's branch.
   */
  branchId?: BranchID;
  /**
   * Static query map. Plain values pass through; values of the form
   * `":name"` are treated as path-param references and resolved at click
   * time from the merge of the current request's path params and any
   * caller-supplied params.
   */
  query?: Record<string, string>;
  /** Carry current URL's search params to the target */
  persistSearch?: boolean;
};

/**
 * Navigate to an external URL (outside the app).
 */
export type ExternalUrlNavigation = {
  /** Full external URL to navigate to */
  externalUrl: string;
};

/**
 * Navigate back using browser history.
 *
 * Falls back to the previous page if the referrer is on the same host.
 */
export type BrowserBackNavigation = {
  browserBack: true;
};

/**
 * Navigation interaction — navigate to a view, external URL, or go back.
 *
 * Union of three variants with a shared `type: "navigation"` discriminant:
 * - {@link ViewNavigation} — navigate to a view by ID
 * - {@link ExternalUrlNavigation} — navigate to an external URL
 * - {@link BrowserBackNavigation} — browser back navigation
 */
export type NavigationInteraction = { type: "navigation" } & (
  | ViewNavigation
  | ExternalUrlNavigation
  | BrowserBackNavigation
);

/**
 * Navigation directive an action's dynamic `next` callback may return
 * **inline** — in place of a sibling interaction key.
 *
 * Restricted to the two variants that need no server-side resolution:
 * - {@link ExternalUrlNavigation} — the destination is computed from the
 *   typed action result at chain time (e.g. an offboarding redirect read off
 *   the response). It can't be pre-resolved on the loader and can't be
 *   declared statically, so returning it inline keeps the URL strongly typed
 *   at the one site where the result type is known.
 * - {@link BrowserBackNavigation} — a pure client action; nothing to resolve.
 *
 * {@link ViewNavigation} is intentionally excluded: its target route is
 * resolved server-side at loader time, so it must be declared as a named
 * navigation interaction and referenced by key from `next`.
 */
export type InlineNavigationTarget =
  | ExternalUrlNavigation
  | BrowserBackNavigation;

/**
 * Refresh interaction — reload the current view's data.
 */
export type RefreshInteraction = {
  type: "refresh";
};

/**
 * Action interaction — execute validated server-side logic at the view level.
 *
 * Wraps an ActionEntry (schema + handler) with a `type: "action"` discriminant
 * so all three interaction variants can be narrowed on the `type` field.
 *
 * Optionally chain an action to a follow-up interaction via `next`.
 * Use the {@link actionInteraction} helper to get full type inference for
 * the `result` parameter in a dynamic `next` callback.
 *
 * @typeParam TSchema - Zod schema for runtime validation and type inference
 * @typeParam TOutput - Return type of the handler
 * @typeParam TNext - String literal type for the `next` chain target
 *
 * @example
 * ```ts
 * import { action, actionInteraction } from "@bestowinc/tinkertoy-core";
 *
 * const view = defineView({
 *   interactions: {
 *     // Simple action — no chaining
 *     trackEvent: {
 *       type: "action",
 *       action: action(z.object({ eventName: z.string() }), async (data, ctx) => { ... }),
 *     },
 *     // Action with static next
 *     submit: actionInteraction(
 *       action(submitSchema, async (data, ctx) => ({ success: true })),
 *       "goToProfile",
 *     ),
 *     // Action with dynamic next — result is fully typed
 *     submitDynamic: actionInteraction(
 *       action(submitSchema, async (data, ctx) => ({ success: true })),
 *       (result) => result.success ? "goToProfile" : "goToError",
 *     ),
 *     // Dynamic next returning an inline external redirect computed from
 *     // the typed result — no named navigation interaction needed.
 *     submitOrOffboard: actionInteraction(
 *       action(submitSchema, async (data, ctx) => ({ offboardingUrl: "..." })),
 *       (result) => result.offboardingUrl
 *         ? { externalUrl: result.offboardingUrl }
 *         : "goToProfile",
 *     ),
 *     goToProfile: { type: "navigation", viewId: "profile" },
 *     goToError: { type: "navigation", viewId: "error" },
 *   },
 * });
 * ```
 */
export type ActionInteraction<
  TSchema extends ZodType = ZodType<void>,
  TOutput = void,
  TNext extends string = string,
> = {
  type: "action";
  action: ActionEntry<TSchema, TOutput>;
  /**
   * Follow-up interaction to execute after this action succeeds. Either a
   * sibling interaction key (static or returned from a callback), or — from
   * a callback only — an {@link InlineNavigationTarget} whose destination is
   * computed from the typed `result`.
   */
  next?: TNext | ((result: TOutput) => TNext | InlineNavigationTarget);
};

/**
 * Union of all view interaction types.
 *
 * All three variants are discriminated on the `type` field:
 * - `"navigation"` — navigate to a URL/view
 * - `"refresh"` — reload current view data
 * - `"action"` — execute validated server-side logic
 */
export type ViewInteraction =
  | NavigationInteraction
  | RefreshInteraction
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- `any` is required so that ActionInteraction<SomeSchema, SomeOutput> is assignable to ViewInteraction
  | ActionInteraction<any, any>;

/**
 * Map of interaction names to their definitions.
 *
 * Declared on the view and passed to the modules function.
 */
export type InteractionMap = Record<string, ViewInteraction>;

// ============================================================================
// Derive Interaction Props
// ============================================================================

/**
 * Derive the component-facing prop type from a single view interaction.
 *
 * - ActionInteraction<TSchema, TOutput> → `(data: z.output<TSchema>) => Promise<TOutput>`
 * - NavigationInteraction → `(params?: Record<string, string>) => void`
 * - RefreshInteraction → `() => void`
 *
 * Navigation handlers accept an optional params object so callers can
 * supply path-param values that aren't already known from the current
 * request (e.g. a row-level `userId` on a list page navigating to a
 * detail page). Keys are loosely typed; the runtime substitutes them
 * into `:name` tokens in the route template and throws a clear error
 * naming the interaction and the missing key if any token is left
 * unresolved. Authors can layer a typed wrapper on top later if static
 * key checks are wanted.
 */
export type DeriveInteractionProp<T extends ViewInteraction> =
  T extends ActionInteraction<infer TSchema, infer TOutput>
    ? (data: z.output<TSchema>) => Promise<TOutput>
    : T extends { type: "navigation" }
      ? (params?: Record<string, string>) => void
      : () => void;

/**
 * Derive component-facing prop types for an entire interaction map.
 *
 * Transforms each interaction definition into the function signature
 * that a module component would receive as a prop.
 *
 * @example
 * ```ts
 * type Interactions = {
 *   goToNext: NavigationInteraction;
 *   refresh: RefreshInteraction;
 *   submit: ActionInteraction<typeof submitSchema, { id: string }>;
 * };
 *
 * type Props = DeriveInteractionProps<Interactions>;
 * // {
 * //   goToNext: () => void;
 * //   refresh: () => void;
 * //   submit: (data: SubmitInput) => Promise<{ id: string }>;
 * // }
 * ```
 */
export type DeriveInteractionProps<T extends InteractionMap> = {
  [K in keyof T]: DeriveInteractionProp<T[K]>;
};

// ============================================================================
// actionInteraction Helper
// ============================================================================

/**
 * Create an action interaction — wraps an ActionEntry with `type: "action"`.
 *
 * Provides full type inference for the `result` parameter in `next` callbacks
 * by capturing TOutput from the ActionEntry in a separate inference context.
 *
 * @overload No chaining — equivalent to `{ type: "action", action: entry }`
 * @overload Static next — chain to a fixed interaction key
 * @overload Dynamic next — chain based on the action result (result is fully typed)
 *
 * @example
 * ```ts
 * import { action, actionInteraction } from "@bestowinc/tinkertoy-core";
 *
 * const submitAction = action(
 *   z.object({ name: z.string() }),
 *   async (data, ctx) => ({ success: true }),
 * );
 *
 * // No next
 * actionInteraction(submitAction)
 *
 * // Static next
 * actionInteraction(submitAction, "goToProfile")
 *
 * // Dynamic next — result is typed as { success: boolean }
 * actionInteraction(submitAction, (result) =>
 *   result.success ? "goToProfile" : "goToError",
 * )
 *
 * // Dynamic next returning an inline external redirect built from the result
 * actionInteraction(submitAction, (result) =>
 *   result.offboardingUrl ? { externalUrl: result.offboardingUrl } : "goToProfile",
 * )
 * ```
 */
export function actionInteraction<TSchema extends ZodType, TOutput>(
  entry: ActionEntry<TSchema, TOutput>,
): Omit<ActionInteraction<TSchema, TOutput>, "next">;

export function actionInteraction<
  TSchema extends ZodType,
  TOutput,
  TNext extends string,
>(
  entry: ActionEntry<TSchema, TOutput>,
  next: TNext,
): ActionInteraction<TSchema, TOutput> & { next: TNext };

export function actionInteraction<
  TSchema extends ZodType,
  TOutput,
  TNext extends string,
>(
  entry: ActionEntry<TSchema, TOutput>,
  next: (result: TOutput) => TNext | InlineNavigationTarget,
): ActionInteraction<TSchema, TOutput> & {
  next: (result: TOutput) => TNext | InlineNavigationTarget;
};

export function actionInteraction<TSchema extends ZodType, TOutput>(
  entry: ActionEntry<TSchema, TOutput>,
  next?: string | ((result: TOutput) => string | InlineNavigationTarget),
): ActionInteraction<TSchema, TOutput> {
  if (next !== undefined) {
    return { type: "action", action: entry, next };
  }
  return { type: "action", action: entry };
}

// ============================================================================
// Type Guards
// ============================================================================

export function isNavigationInteraction(
  interaction: ViewInteraction,
): interaction is NavigationInteraction {
  return interaction.type === "navigation";
}

export function isRefreshInteraction(
  interaction: ViewInteraction,
): interaction is RefreshInteraction {
  return interaction.type === "refresh";
}

export function isActionInteraction(
  interaction: ViewInteraction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the union's `ActionInteraction<any, any>`; preserves the unknown-result/unknown-schema reality at the runtime narrowing seam
): interaction is ActionInteraction<any, any> {
  return interaction.type === "action";
}
